/**************************************************************************************************
 *
 *  This is the main container for all project related views: Project Detail (hierarch), Project
 *  Edit/Creation, Build Edit/Creation, Task Detail, Field Task Detail
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
**************************************************************************************************/

import React from 'react';
import { Router, Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import dataModel from '../../models/DataModel';
import mobileBridge from '../../helpers/MobileBridge';
import ReduxConstants from '../../constants/ReduxConstants.js';
import Home from '../../containers/Home';
import BasePage from '../../baseClasses/BasePage';
import Header from '../../components/Header';
import animator from '../../helpers/animator';
import ProjectsNav from '../../components/ProjectsNav';
import ErrorPage from '../ErrorPage';

import PageStateDAO from '../../dao/PageStateDAO';
import { PageNames } from '../../constants/ReduxConstants';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import { projects_nav_root } from '../../dao/collections/FavoritesCollection';
import { project_root } from '../../dao/ProjectsDAO';

import addIcon from '../../assets/images/add-icon-gray.svg';
import projectIcon from '../../assets/images/projects-icon-light-gray.svg';
import companyIcon from '../../assets/images/companies-icon-light-gray.svg';
import './ProjectsContainer.scss';
import CollectionDataSource from '../../dao/DataSource/CollectionDataSource';
import projectsService from '../../services/projectsService';

const projectDescriptor = { ...project_root };
projectDescriptor.project.additionalFields = ['has_alerts', 'has_unread_alerts', 'has_delays'];

const REFETCH_FAVORITES_DELAY_MS = 400;
// Used to compute projects perPage when favorites count unknown (e.g. initial parallel load)
const DEFAULT_FAVORITES_OFFSET_PX = 28 + 48;
// Limit concurrent getProject calls when enriching favorites progress to avoid connection/server overload
const FAVORITES_PROGRESS_FETCH_CONCURRENCY = 5;
// If progress fetch takes longer, show favorites without enrichment rather than hang
const FAVORITES_PROGRESS_FETCH_TIMEOUT_MS = 15000;

/**
 * Run an array of promise-returning functions with a concurrency limit.
 * @param {Array<() => Promise<any>>} fns
 * @param {number} concurrency
 * @returns {Promise<Array<any>>}
 */
function runWithConcurrency(fns, concurrency) {
  const results = [];
  let index = 0;
  function runNext() {
    if (index >= fns.length) return Promise.resolve();
    const i = index++;
    return fns[i]().then((value) => { results[i] = value; }).then(runNext);
  }
  const workers = Array(Math.min(concurrency, fns.length)).fill(null).map(() => runNext());
  return Promise.all(workers).then(() => results);
}

export class ProjectsContainer extends BasePage {

  constructor(props) {
    super(props);

    this.state = {
      view: null,
      resetScroll: false,
    };

    this.animator = new animator();
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.mounted = false;
  }

  handleDisplayAddProject = () => {
    if (this.projectsNavCallback) {
      this.projectsNavCallback();
    }
  }

  setProjectsNavCallback = (callback) => {
    this.projectsNavCallback = callback;
  }

  handleProjectsUpdate(projects, action, acData, fetchedData) {
    this.setState({ navigationProjects: projects, loading: false });
    const currentUser = this.props.currentUser;
    if (acData && acData.sub_type == 'change_favorite' && currentUser && acData.updating_user_id == currentUser.id) {
      this.fetchFavoritesFromApi();
    }
  }

  /**
   * Fetch favorites from the API and enrich with task counts when the API returns 0/0 or omits them.
   * Uses getProject (service only, no store) for missing progress to avoid Redux persist quota.
   *
   * Audit: (1) Edge cases: id normalized to String for byId lookup so number/string ids match.
   * (2) Failure: getFavorites error keeps previous list if we had one; progress fetch timeout
   * applies list without enrichment; per-project getProject failure leaves that item as 0/0.
   * (3) Assumptions: API returns { data: [...] } or array; getProject returns id + task counts.
   * (4) Security: no new data exposure; getProject is auth’d. (5) Performance: concurrency cap
   * on getProject (5) and 15s timeout. (6) Readability: comment for 0/0 logic; apply guarded by mounted.
   */
  fetchFavoritesFromApi() {
    if (!this.mounted || !this.props.currentUser || !this.props.currentCompany) return;
    const options = { recordDescriptor: projects_nav_root };
    projectsService.getFavorites(null, options)
      .then((response) => {
        if (!this.mounted) return;
        if (!response || response.error) {
          this.setState({ favoriteProjects: { projects: [], ids: [] } });
          return;
        }
        const raw = response.data;
        const data = Array.isArray(response) ? response : (Array.isArray(raw) ? raw : (raw && raw.data) || []);
        const projects = data
          .map((item) => {
            const id = item.id != null ? item.id : (item.attributes && item.attributes.id);
            if (id == null) return null;
            const attrs = item.attributes || {};
            const tasks_count = attrs.tasks_count != null ? attrs.tasks_count : item.tasks_count;
            const tasks_completed_count = attrs.tasks_completed_count != null ? attrs.tasks_completed_count : item.tasks_completed_count;
            const completion_steps_count = attrs.completion_steps_count != null ? attrs.completion_steps_count : item.completion_steps_count;
            const completion_steps_completed_count = attrs.completion_steps_completed_count != null ? attrs.completion_steps_completed_count : item.completion_steps_completed_count;
            return {
              ...attrs,
              id,
              is_favorite: true,
              ...(tasks_count != null && { tasks_count }),
              ...(tasks_completed_count != null && { tasks_completed_count }),
              ...(completion_steps_count != null && { completion_steps_count }),
              ...(completion_steps_completed_count != null && { completion_steps_completed_count }),
            };
          })
          .filter(Boolean);
        const existingFavs = (this.state.favoriteProjects && this.state.favoriteProjects.projects) || [];
        const existingNav = (this.state.navigationProjects && this.state.navigationProjects.projects) || [];
        const projectsWithProgress = projects.map((p) => {
          const hasProgress = (p.completion_steps_count != null && p.completion_steps_completed_count != null) ||
            (p.tasks_count != null && p.tasks_completed_count != null);
          if (hasProgress) return p;
          const existing = existingFavs.find((e) => e.id == p.id) || existingNav.find((e) => e.id == p.id);
          if (existing) {
            const fromExisting = {
              ...(existing.tasks_count != null && { tasks_count: p.tasks_count != null ? p.tasks_count : existing.tasks_count }),
              ...(existing.tasks_completed_count != null && { tasks_completed_count: p.tasks_completed_count != null ? p.tasks_completed_count : existing.tasks_completed_count }),
              ...(existing.completion_steps_count != null && { completion_steps_count: p.completion_steps_count != null ? p.completion_steps_count : existing.completion_steps_count }),
              ...(existing.completion_steps_completed_count != null && { completion_steps_completed_count: p.completion_steps_completed_count != null ? p.completion_steps_completed_count : existing.completion_steps_completed_count }),
            };
            if (Object.keys(fromExisting).length) return { ...p, ...fromExisting };
          }
          return p;
        });
        const sorted = [...projectsWithProgress].sort((a, b) =>
          (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
        );
        // Favorites API often returns 0/0 or omits step counts; treat as missing so we fetch from getProject
        const missingProgress = sorted.filter((p) => {
          const hasSteps = p.completion_steps_count != null && p.completion_steps_completed_count != null && p.completion_steps_count > 0;
          const hasTasks = p.tasks_count != null && p.tasks_completed_count != null && p.tasks_count > 0;
          if (hasSteps || hasTasks) return false;
          const missing = (p.tasks_count == null || p.tasks_completed_count == null) &&
            (p.completion_steps_count == null || p.completion_steps_completed_count == null);
          const zeroZero = (p.tasks_count === 0 && p.tasks_completed_count === 0) &&
            (p.completion_steps_count === 0 || p.completion_steps_count == null);
          return missing || zeroZero;
        });
        const applyFavoritesAndPerPage = (projectsList) => {
          if (!this.mounted) return;
          const favoriteProjects = { projects: projectsList, ids: projectsList.map((p) => p.id) };
          let favoritesSize = ((Math.min(projectsList.length, 2) * 82) + 28);
          favoritesSize += (favoritesSize > 28 ? 3 : 48);
          const perPage = Math.max(1, Math.floor((window.innerHeight - 160 - favoritesSize) / 82) + 1);
          this.setState({ favoriteProjects, perPage });
          if (this.projectsDataSource) {
            this.projectsDataSource.perPage = perPage;
          }
        };
        if (missingProgress.length === 0) {
          applyFavoritesAndPerPage(sorted);
          return;
        }
        const navOptions = { recordDescriptor: projects_nav_root };
        const fetchOne = (p) => () =>
          projectsService.getProject(p.id, null, navOptions)
            .then((raw) => {
              if (!raw || raw.error) return null;
              const data = raw.data || raw;
              const attrs = data.attributes || data;
              const tasks_count = attrs.tasks_count != null ? attrs.tasks_count : data.tasks_count;
              const tasks_completed_count = attrs.tasks_completed_count != null ? attrs.tasks_completed_count : data.tasks_completed_count;
              const completion_steps_count = attrs.completion_steps_count != null ? attrs.completion_steps_count : data.completion_steps_count;
              const completion_steps_completed_count = attrs.completion_steps_completed_count != null ? attrs.completion_steps_completed_count : data.completion_steps_completed_count;
              const hasProgress = (completion_steps_count != null && completion_steps_completed_count != null) ||
                (tasks_count != null && tasks_completed_count != null);
              if (!hasProgress) return null;
              const id = data.id != null ? data.id : raw.id;
              if (id == null) return null;
              return {
                id,
                tasks_count: tasks_count != null ? tasks_count : null,
                tasks_completed_count: tasks_completed_count != null ? tasks_completed_count : null,
                completion_steps_count: completion_steps_count != null ? completion_steps_count : null,
                completion_steps_completed_count: completion_steps_completed_count != null ? completion_steps_completed_count : null,
              };
            })
            .catch(() => null);
        let settled = false;
        const timeoutId = setTimeout(() => {
          if (settled || !this.mounted) return;
          settled = true;
          applyFavoritesAndPerPage(sorted);
        }, FAVORITES_PROGRESS_FETCH_TIMEOUT_MS);
        runWithConcurrency(missingProgress.map((p) => fetchOne(p)), FAVORITES_PROGRESS_FETCH_CONCURRENCY)
          .then((results) => {
            if (settled || !this.mounted) return;
            settled = true;
            clearTimeout(timeoutId);
            const byId = {};
            results.forEach((row) => {
              if (row && row.id != null) {
                byId[String(row.id)] = {
                  tasks_count: row.tasks_count,
                  tasks_completed_count: row.tasks_completed_count,
                  completion_steps_count: row.completion_steps_count,
                  completion_steps_completed_count: row.completion_steps_completed_count,
                };
              }
            });
            const enriched = sorted.map((p) => {
              const progress = byId[String(p.id)];
              if (!progress) return p;
              return {
                ...p,
                ...(progress.tasks_count != null && { tasks_count: progress.tasks_count }),
                ...(progress.tasks_completed_count != null && { tasks_completed_count: progress.tasks_completed_count }),
                ...(progress.completion_steps_count != null && { completion_steps_count: progress.completion_steps_count }),
                ...(progress.completion_steps_completed_count != null && { completion_steps_completed_count: progress.completion_steps_completed_count }),
              };
            });
            applyFavoritesAndPerPage(enriched);
          });
      })
      .catch(() => {
        if (!this.mounted) return;
        if (this.state.favoriteProjects && this.state.favoriteProjects.projects && this.state.favoriteProjects.projects.length > 0) {
          return;
        }
        this.setState({ favoriteProjects: { projects: [], ids: [] } });
      });
  }

  handleFavoritesUpdate(favoriteProjects, action, acData, fetchedData) {
    // Sidebar favorites come from fetchFavoritesFromApi(); still support collection callback for refetch path
    let trueFavoriteProjects = Object.assign({}, favoriteProjects);
    trueFavoriteProjects.projects = (trueFavoriteProjects.projects || []).map(project =>
      project ? { ...project, is_favorite: true } : project
    ).filter(Boolean);
    trueFavoriteProjects.ids = (trueFavoriteProjects.projects || []).map(p => p.id);
    trueFavoriteProjects = this.sortFavoritesByName(trueFavoriteProjects);
    let favoritesSize = ((Math.min(trueFavoriteProjects.ids.length, 2) * 82) + 28);
    favoritesSize += (favoritesSize > 28 ? 3 : 48);
    const perPage = Math.floor((window.innerHeight - 160 - favoritesSize) / 82) + 1;
    this.setState({ favoriteProjects: trueFavoriteProjects, perPage });
    if (!acData && this.projectsDataSource) {
      this.projectsDataSource.perPage = perPage;
    }
  }

  handleFinishInitialization(fromMount) {
    const { currentUser, currentCompany } = this.props;
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    if (!currentCompany || !currentUser) {
      return null;
    }

    this.setState({ loading: true, favoriteProjects: null });
    if (currentRole && currentRole.name === 'view_only') {
      this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_nav_root, this.handleProjectsUpdate, ReduxConstants.PROJECTS_NAV, null, null, currentUser.id, currentCompany.id, 'projects');
    }
    else{
      this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_nav_root, this.handleProjectsUpdate, ReduxConstants.PROJECTS_NAV, null, null, null, currentCompany.id, 'projects', null, projects_nav_root, true);
    }
    this.projectsDataSource.searchString = '';
    this.projectsDataSource.filter = {};

    this.favoritesDataSource = new CollectionDataSource('FavoritesCollection', 'Project', projects_nav_root, this.handleFavoritesUpdate, currentUser.id, currentCompany.id);
    this.projectsDataSource.perPage = Math.floor((window.innerHeight - 160 - DEFAULT_FAVORITES_OFFSET_PX) / 82) + 1;
    this.fetchFavoritesFromApi();
    this.projectsDataSource.current();
  }

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;
    this._ensuredProjectId = null;
    this.handleFinishInitialization(true);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.currentCompany && prevProps.currentCompany && prevProps.currentCompany.id != this.props.currentCompany.id) {
      this.releaseProjectDataSources();
      this.handleFinishInitialization();
      this._ensuredProjectId = null;
    }
    this.ensureCurrentProjectInNav();
  }

  releaseProjectDataSources() {
    if (this.projectsDataSource) {
      this.projectsDataSource.release();
      this.projectsDataSource = null;
    }
    if (this.favoritesDataSource) {
      this.favoritesDataSource.release();
      this.favoritesDataSource = null;
    }
  }

  /**
   * After refresh, the current URL project may be missing from both nav lists. Fetch it once and add
   * it to the PROJECTS list only so it appears in the sidebar. We never add to favorites here—the
   * favorites API is the single source of truth for the favorites list.
   */
  ensureCurrentProjectInNav() {
    const { navigationProjects, favoriteProjects } = this.state;
    if (!navigationProjects || !favoriteProjects || !this.mounted) return;
    const path = this.props.history && this.props.history.parseParameters ? this.props.history.parseParameters() : null;
    const projectId = path && path.params && path.params.project_id ? path.params.project_id : null;
    const idNum = projectId != null && projectId !== '' ? Number(projectId) : NaN;
    if (!projectId || Number.isNaN(idNum) || idNum <= 0 || this._ensuredProjectId === projectId) return;
    const navIds = (navigationProjects.ids || []).map(id => Number(id));
    const favIds = (favoriteProjects.ids || []).map(id => Number(id));
    const idsInNav = new Set([...navIds, ...favIds]); // O(1) lookup at scale
    if (idsInNav.has(idNum)) return;
    this._ensuredProjectId = projectId;
    dataModel.projectsDAO.getProject(projectId, { recordDescriptor: projects_nav_root }).then(expandedProject => {
      if (!this.mounted || !expandedProject || !expandedProject.id) return;
      const { selectedProject, navigationProjects: nav } = this.state;
      if (!nav || !nav.projects) return;
      const foundInNav = nav.projects.some(p => Number(p.id) === Number(expandedProject.id));
      if (foundInNav) return;
      const nextProjects = [...(nav.projects || []), expandedProject].sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      const nextNav = { ...nav, projects: nextProjects, ids: nextProjects.map(p => p.id) };
      this.setState({ navigationProjects: nextNav });
      if (selectedProject && selectedProject.id == expandedProject.id) {
        this.setState({ selectedProject: expandedProject });
        this.handleRegisterSelectedProject(expandedProject);
      }
    }).catch(() => {
      // Do not reset _ensuredProjectId so we don't retry in a loop on 404/network errors
    });
  }

  componentWillUnmount() {
    this.mounted = false;
    this.releaseProjectDataSources();
    PageStateDAO.setPageStateEntry(PageNames.PROJECTS_CONTAINER, 'mapExpanded', false);
  }

  handlePutProjectWhereItBelongs(expandedProject, selectedProject, navigationProjects, favoriteProjects) {
    if (!expandedProject) return;
    if (expandedProject.is_favorite) {
      if (!favoriteProjects.projects || favoriteProjects.projects.length == 0) {
        favoriteProjects.projects = [expandedProject];
        favoriteProjects.ids = favoriteProjects.ids || [];
        if (favoriteProjects.ids.indexOf(expandedProject.id) === -1) {
          favoriteProjects.ids = [...favoriteProjects.ids, expandedProject.id];
        }
      } else {
        let found = false;
        favoriteProjects.projects = favoriteProjects.projects.map(project => {
          if (project.id == expandedProject.id) {
            found = true;
            return expandedProject;
          }
          return project;
        });
        if (!found) {
          favoriteProjects.projects = [...(favoriteProjects.projects || []), expandedProject];
          favoriteProjects.ids = favoriteProjects.ids || [];
          favoriteProjects.ids = [...favoriteProjects.ids, expandedProject.id];
        }
      }
      // Remove from PROJECTS list so it only appears under FAVORITES
      const navProjects = (navigationProjects.projects || []).filter(p => p.id != expandedProject.id);
      const navIds = (navigationProjects.ids || []).filter(id => id != expandedProject.id);
      const totalProjects = navigationProjects.totalProjects != null && navigationProjects.totalProjects > 0
        ? navigationProjects.totalProjects - 1
        : (navigationProjects.totalProjects != null ? navigationProjects.totalProjects : navProjects.length);
      const nextNav = {
        ...navigationProjects,
        projects: navProjects,
        ids: navIds,
        totalProjects,
        ...(navigationProjects.totalRecords != null && { totalRecords: totalProjects }),
      };
      this.setState({ favoriteProjects: this.sortFavoritesByName(favoriteProjects), navigationProjects: nextNav });
      if (selectedProject && selectedProject.id == expandedProject.id) {
        this.setState({ selectedProject: expandedProject });
        this.handleRegisterSelectedProject(expandedProject);
      }
    }
    else {
      const existingInFavs = favoriteProjects.projects && favoriteProjects.projects.find((p) => p.id == expandedProject.id);
      if (existingInFavs && (existingInFavs.tasks_count != null || existingInFavs.tasks_completed_count != null)) {
        expandedProject = {
          ...expandedProject,
          tasks_count: expandedProject.tasks_count != null ? expandedProject.tasks_count : existingInFavs.tasks_count,
          tasks_completed_count: expandedProject.tasks_completed_count != null ? expandedProject.tasks_completed_count : existingInFavs.tasks_completed_count,
        };
      }
      if (!navigationProjects.projects || navigationProjects.projects.length == 0) {
        navigationProjects.projects = [expandedProject];
        navigationProjects.ids = (navigationProjects.ids || []).concat(expandedProject.id);
        if (navigationProjects.totalProjects != null) {
          navigationProjects.totalProjects++;
        }
        if (navigationProjects.totalRecords != null) {
          navigationProjects.totalRecords = navigationProjects.totalProjects;
        }
      } else {
        let foundInNav = false;
        navigationProjects.projects = navigationProjects.projects.map(project => {
          if (project.id == expandedProject.id) {
            foundInNav = true;
            return expandedProject;
          }
          return project;
        });
        if (!foundInNav) {
          const sorted = [...(navigationProjects.projects || []), expandedProject].sort((a, b) =>
            (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
          );
          navigationProjects.projects = sorted;
          navigationProjects.ids = sorted.map(p => p.id);
          if (navigationProjects.totalProjects != null) {
            navigationProjects.totalProjects++;
          }
          if (navigationProjects.totalRecords != null) {
            navigationProjects.totalRecords = navigationProjects.totalProjects;
          }
        }
      }
      if (favoriteProjects.projects && favoriteProjects.projects.length) {
        favoriteProjects.projects = favoriteProjects.projects.filter(p => p.id != expandedProject.id);
        favoriteProjects.ids = (favoriteProjects.ids || []).filter(id => id != expandedProject.id);
        this.setState({ favoriteProjects: this.sortFavoritesByName(favoriteProjects) });
      }
      this.setState({ navigationProjects: navigationProjects });
      if (selectedProject && selectedProject.id == expandedProject.id) {
        this.setState({ selectedProject: expandedProject });
        this.handleRegisterSelectedProject(expandedProject);
      }
    }
  }

  sortFavoritesByName(favoriteProjects) {
    if (!favoriteProjects.projects || favoriteProjects.projects.length === 0) return favoriteProjects;
    const sorted = [...favoriteProjects.projects].sort((a, b) =>
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    );
    return { ...favoriteProjects, projects: sorted, ids: sorted.map(p => p.id) };
  }

  notifySidePanelProjectUpdated(projectId) {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('projectFavoriteUpdated', { detail: { projectId } }));
    }
  }

  handleGetNextPage() {
    if (this.projectsDataSource) {
      return this.projectsDataSource.next().then(navigationProjects => {
        return navigationProjects;
      });
    }
  }

  handleFilterProjects(filter) {
    if (this.projectsDataSource) {
      let favoritesSize = ((Math.min(this.state.favoriteProjects && this.state.favoriteProjects.ids ? this.state.favoriteProjects.ids.length : 0, 2) * 82) + 28);
      favoritesSize += (favoritesSize > 28 ? 3 : 48);
      const perPage = Math.floor((window.innerHeight - 160 - favoritesSize) / 82) + 1;
      this.projectsDataSource.perPage = perPage;
      if (filter) {
        const { project_type, state, showArchived, statuses } = filter;
        this.projectsDataSource.filter = {
          projectTypeId: project_type ? project_type.id : null,
          state,
          showArchived,
          statuses,
        };
      }
      else {
        this.projectsDataSource.filter = {
          projectTypeId: null,
          state: null,
          showArchived: null,
          statuses: null,
        };
      }
      return this.projectsDataSource.current().then(navigationProjects => {
        return navigationProjects;
      });
    }
    return this.fetchData(true);
  }

  handleProjectSearch(filter, searchString, perPage) {
    if (this.projectsDataSource) {
      let favoritesSize = ((Math.min(this.state.favoriteProjects && this.state.favoriteProjects.ids ? this.state.favoriteProjects.ids.length : 0, 2) * 82) + 28);
      favoritesSize += (favoritesSize > 28 ? 3 : 48);
      perPage = Math.floor((window.innerHeight - 160 - favoritesSize) / 82) + 1;
      this.projectsDataSource.perPage = perPage;

      if (!searchString) {
        this.projectsDataSource.searchString = null;
      }
      else {
        this.projectsDataSource.searchString = searchString;
      }
      return this.projectsDataSource.current().then(navigationProjects => {
        return navigationProjects;
      });
    }
  }

  setFavoriteAndUpdateUI(project_id, favoriteValue, analyticsEvent) {
    const options = { recordDescriptor: projects_nav_root };
    dataModel.projectsDAO.setFavoriteProject(project_id, favoriteValue, null, options)
      .then((updatedProject) => {
        const fromStore = dataModel.projectsDAO.findItem(project_id);
        const storeMatches = fromStore && fromStore.is_favorite === favoriteValue;
        let projectToUse = updatedProject
          || (storeMatches ? dataModel.projectsDAO.mapToReturn(fromStore, options) : null);

        if (projectToUse) {
          const existingInNav = (this.state.navigationProjects && this.state.navigationProjects.projects || []).find(p => p.id == project_id);
          const existingInFavs = (this.state.favoriteProjects && this.state.favoriteProjects.projects || []).find(p => p.id == project_id);
          const hasProgress = (p) => p && (p.tasks_count != null || p.tasks_completed_count != null) && !(p.tasks_count === 0 && p.tasks_completed_count === 0);
          if (favoriteValue) {
            if (existingInNav && (projectToUse.tasks_count == null || projectToUse.tasks_completed_count == null)) {
              projectToUse = {
                ...projectToUse,
                tasks_count: projectToUse.tasks_count != null ? projectToUse.tasks_count : existingInNav.tasks_count,
                tasks_completed_count: projectToUse.tasks_completed_count != null ? projectToUse.tasks_completed_count : existingInNav.tasks_completed_count,
              };
            }
          } else {
            if (existingInFavs && hasProgress(existingInFavs)) {
              projectToUse = {
                ...projectToUse,
                tasks_count: projectToUse.tasks_count != null ? projectToUse.tasks_count : existingInFavs.tasks_count,
                tasks_completed_count: projectToUse.tasks_completed_count != null ? projectToUse.tasks_completed_count : existingInFavs.tasks_completed_count,
              };
            }
          }
          const favoriteProjects = this.state.favoriteProjects
            ? { ...this.state.favoriteProjects, projects: [...(this.state.favoriteProjects.projects || [])], ids: [...(this.state.favoriteProjects.ids || [])] }
            : { projects: [], ids: [] };
          const navigationProjects = this.state.navigationProjects
            ? { ...this.state.navigationProjects, projects: [...(this.state.navigationProjects.projects || [])], ids: [...(this.state.navigationProjects.ids || [])] }
            : { projects: [], ids: [] };
          const selectedProject = this.state.selectedProject || {};
          const applyUpdate = () => {
            this.handlePutProjectWhereItBelongs(projectToUse, selectedProject, navigationProjects, favoriteProjects);
            this.notifySidePanelProjectUpdated(project_id);
          };
          const needsProgressFetch = !favoriteValue && !hasProgress(projectToUse);
          if (needsProgressFetch) {
            projectsService.getProject(project_id, null, options)
              .then((raw) => {
                if (!this.mounted || !raw || raw.error) { applyUpdate(); return; }
                const data = raw.data || raw;
                const attrs = data.attributes || data;
                const tc = attrs.tasks_count != null ? attrs.tasks_count : data.tasks_count;
                const tcc = attrs.tasks_completed_count != null ? attrs.tasks_completed_count : data.tasks_completed_count;
                if (tc != null || tcc != null) {
                  projectToUse = { ...projectToUse, tasks_count: tc, tasks_completed_count: tcc };
                }
                applyUpdate();
              })
              .catch(() => applyUpdate());
          } else {
            applyUpdate();
          }
        }
        this.analyticsLogger.logProjectListEvent(analyticsEvent);
        setTimeout(() => {
          this.fetchFavoritesFromApi();
        }, REFETCH_FAVORITES_DELAY_MS);
      })
      .catch(() => {});
  }

  handleRemoveFavorite(project_id) {
    this.setFavoriteAndUpdateUI(project_id, false, 'ProjectListUnfavorite');
  }

  handleMakeFavorite(project_id) {
    this.setFavoriteAndUpdateUI(project_id, true, 'ProjectListFavorite');
  }

  async handleSaveProject(updatedProjectId) {
    const { selectedProject, navigationProjects, favoriteProjects } = this.state;
    dataModel.projectsDAO.getProject(updatedProjectId, { recordDescriptor: projectDescriptor }).then(updatedProject => {
      if (updatedProject) {
        this.handlePutProjectWhereItBelongs(updatedProject, selectedProject, navigationProjects, favoriteProjects);
      }
    });
  }

  getSystemPopupView() {
    if (this.props.systemPopup) {
      return this.props.systemPopup;
    }
    else {
      return null;
    }
  }

  handleProjectAdd(project, isCopied) {

    if (dataModel.projectsDAO.clearCache) {
      dataModel.projectsDAO.clearCache();
    }

    // Clear out any previous data sources
    if (this.projectsDataSource) {
      this.projectsDataSource.release();
      this.projectsDataSource = null;
    }

    // Get the role and then recreate the data source
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    const { currentUser, currentCompany } = this.props;

    if(currentRole.name === 'view_only'){
      this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_nav_root, this.handleProjectsUpdate, ReduxConstants.PROJECTS_NAV, 13, null, currentUser.id, currentCompany.id, 'projects');
    }
    else{
      this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_nav_root, this.handleProjectsUpdate, ReduxConstants.PROJECTS_NAV, 13, null, null, currentCompany.id, 'projects', null, projects_nav_root, true);
    }
    this.projectsDataSource.searchString = '';
    this.projectsDataSource.filter = {};

    this.projectsDataSource.current().then(projects => {
      this.setState({
        navigationProjects: projects,
        selectedProject: project,
        resetScroll: !this.state.resetScroll,
      });
      this.props.history.push(`/projects/${project.id}/edit${!isCopied ? '?isProjectNew=true' : ''}`);
    });
  }

  handleProjectDelete(deletedProjectId){
    // If we're currently viewing the deleted project, navigate away immediately so no GET is issued for the deleted id
    const path = this.props.history && this.props.history.parseParameters ? this.props.history.parseParameters() : null;
    const currentProjectId = path && path.params && path.params.project_id ? Number(path.params.project_id) : null;
    if (deletedProjectId != null && currentProjectId != null && currentProjectId === deletedProjectId) {
      this.props.history.push('/projects/summary');
    }
    // Force a real server refetch. markDeleted only flags the project DELETED in
    // the store; it does not decrement the collection's total_projects, and
    // needsFetch still treats the collection as SYNCED (the DELETED item is not
    // NEEDS_SYNC). A non-forced current() would return the cached collection, so
    // denormalization drops the project from `projects` (3) while totalProjects
    // stays stale (4). InfiniteScroll then sees projects.length < totalProjects
    // forever and the project-nav-loading-indicator loader never clears.
    this.projectsDataSource.current(null, true).then(projects => {
      this.setState({ navigationProjects: projects });
      dataModel.requiredDataNotAvailable = false;
      this.fetchFavoritesFromApi();
      // If current URL is still a project that no longer exists (e.g. race or no id passed), navigate away
      const pathAfter = this.props.history && this.props.history.parseParameters ? this.props.history.parseParameters() : null;
      const projectIdAfter = pathAfter && pathAfter.params && pathAfter.params.project_id ? Number(pathAfter.params.project_id) : null;
      if (projectIdAfter != null) {
        const stillExists = (projects && projects.projects && projects.projects.some(p => p.id == projectIdAfter));
        if (!stillExists) {
          this.props.history.push('/projects/summary');
        }
      }
    });
  }

  render() {
    const { loading } = this.props;
    const currentPath = this.props.history.getCurrentLocation().pathname;
    if (currentPath == '/login') {
      return null;
    }

    if (!mobileBridge) {
      return null;
    }
    else if (mobileBridge.isMobile) {
      return null;
    }
    if (this.props.catastrophicError) {
      return <ErrorPage catastrophicError={this.props.catastrophicError} />;
    }
    let waitSpinner = null;
    if (loading) {
      waitSpinner = <Home type="transparent" />;
    }
    if (!dataModel.textMap) {
      dataModel.textMap = this.textMap;
    }
    if (!this.props.currentUser || !this.props.currentCompany || !this.props.permissions) {
      return <Home type={'projects'} message={'Awaiting Initialization'} />;
    }

    if (!this.props.permissions.canViewProject) {
      this.props.history.replace('/tasks/project/all');
    }
    if (this.props.permissions.canDeleteProject && !this.props.permissions.canEditProject){
      this.props.permissions.canEditProject = true;
    }
    this.setWhiteLabels(this.props.currentCompany);

    if (!this.state.navigationProjects && this.props.history._history.location.pathname != '/login') {
      return <Home type={'projects'} message={'LOADING PROJECTS'} />;
    }

    const {
      favoriteProjects,
      navigationProjects,
    } = this.state;

    let view = null;

    if (this.props.permissions && this.props.currentCompany.admin_company) {
      view = <div className="project-hierarchy" id={this.props.id}>
        <div className='no-projects-container'>
          <img className='project-icon' src={companyIcon} alt='icon' />
          <div className="no-projects">To edit/view projects you must first select a company.</div>
        </div>
      </div>;
    }
    else if (!loading && (!navigationProjects || !navigationProjects.ids.length) && (!favoriteProjects || !favoriteProjects.ids.length)) {
      let noProjectsText = <div className="no-projects">There are no projects assigned to you.<br />Check the tasks page to see if you are assigned <br />to projects within an affliliate company.</div>;
      if (this.props.permissions.canCreateProject) {
        noProjectsText = <div className="no-projects">There are no projects created yet for this company. <br />Select <img alt='button' className='add-button' src={addIcon} onClick={this.handleDisplayAddProject}/> to add a project.</div>;
      }
      view = <div className="project-hierarchy" id={this.props.id}>
        <div className='no-projects-container'>
          <img className='project-icon' src={projectIcon} alt='icon' />
          {noProjectsText}
        </div>
      </div>;
    }

    const path = this.props.history.parseParameters();

    const paramsString = path.params.project_id + (path.params.build_id ? '_'+ path.params.build_id : '') + (path.params.task_id ? '_' + path.params.task_id : '');

    // UPDATE SQUASH ///
    const projectNavigator =
      <ProjectsNav /*ref={component => {this.navComponent = component}}*/
        onRegisterCallback={this.setProjectsNavCallback}
        navigationProjects={navigationProjects}
        resetScroll={this.state.resetScroll}
        favorites={favoriteProjects}
        selectedProjectId={path.params.project_id}
        selectedCompany={(this.props.selectedCompany ? this.props.selectedCompany : this.props.currentCompany)}
        handleProjectAdd={this.handleProjectAdd}
        addButtonEnabled={true}
        inProjectsTab={true}
        getNextPage={this.handleGetNextPage}
        handleProjectSearch={this.handleProjectSearch}
        handleFilterProjects={this.handleFilterProjects}
        handleMakeFavorite={this.handleMakeFavorite}
        handleRemoveFavorite={this.handleRemoveFavorite}
        viewName={path.params.edit ? 'projectCRUD' : ''}
        handleProjectDelete={this.handleProjectDelete}
      />;
    const systemPopup = this.getSystemPopupView();

    let header = null;
    let isBulkAssign = false;
    if ((this.state.bulkAssign == 'build_owner' || this.state.bulkAssign == 'notify_build')
        && this.state.selectedUser
    ) {
      isBulkAssign = true;
    }
    header = <Header selectedPage="projects" isBulkAssign={isBulkAssign} />;
    return (
      <div className="projects-container"
        ref={(element) => { this.mainContainer = element; }}>
        {systemPopup}
        {header}
        {waitSpinner}
        <div className="projects-body">
          <div className={'nav-container'}>
            {projectNavigator}
          </div>
          <div className="detail-container" id="projects-detail-container" >
          {view ||
            <Router history={this.props.history}>
              <Switch>
                {this.props.routes.map((route, i) => (
                  <Route
                    key={ route.component.displayName ? route.component.displayName : route.component.name}
                    path={route.path}
                    render={props => {
                      let routeProps = { ...props };
                      routeProps.params = path.params;
                      routeProps.id = paramsString;
                      routeProps.key = paramsString;
                      routeProps.history = this.props.history;

                      if (path.params.project_id && path.params.edit) {
                        routeProps.isDialog = true;
                        routeProps.handleSaveProject = this.handleSaveProject;
                      }
                      return (
                        <route.component {...routeProps} routes={route.routes} />
                      );
                    }
                    }
                  />
                )
                )}
              </Switch>
            </Router>
            }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    catastrophicError: state.main.catastrophicError,
    permissions: state.new_users ? state.new_users.permissions : null,
    systemPopup: state.main.systemPopup,
    currentUser: state.new_users.currentUser,
    passthroughUri: state.main.passthroughUri,
    currentCompany: state.new_companies.currentCompany,
    needsLogin: state.main ? state.main.needsLogin : null,
    dataChanged: state.main.dataChanged,
  };
};

export default withRouter(connect(mapStateToProps)(ProjectsContainer));



// WEBPACK FOOTER //
// ./src/containers/ProjectsContainer/index.js